import React, { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Grid } from '@mui/material';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import ContactPhoneOutlinedIcon from '@mui/icons-material/ContactPhoneOutlined';
import GavelOutlinedIcon from '@mui/icons-material/GavelOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined';
import { api } from 'common';
import CircularLoading from '../components/CircularLoading';
import {
  DemoModeAlert,
  SettingsCard,
  SettingsField,
  SettingsNotice,
  SettingsSaveBar,
  isEmail,
  isUrl,
  useSettingsDraft,
} from '../components/settings/C2SettingsKit';

const URL_FIELDS = ['CompanyWebsite', 'CompanyTerms', 'CompanyTermCondition', 'AppleStoreLink', 'PlayStoreLink', 'FacebookHandle', 'TwitterHandle', 'InstagramHandle'];
const REQUIRED_FIELDS = { appName: 'Escribe el nombre de la app.', CompanyName: 'Escribe el nombre de la empresa.' };

const validate = (d) => {
  const errors = {};
  if (!d) return errors;
  Object.keys(REQUIRED_FIELDS).forEach((k) => {
    if (!String(d[k] || '').trim()) errors[k] = REQUIRED_FIELDS[k];
  });
  URL_FIELDS.forEach((k) => {
    if (String(d[k] || '').trim() && !isUrl(d[k])) errors[k] = 'Debe ser un enlace completo, por ejemplo https://…';
  });
  if (String(d.contact_email || '').trim() && !isEmail(d.contact_email)) errors.contact_email = 'Correo no válido.';
  if (String(d.CompanyPhone || '').trim() && !/^\+?[\d\s()-]{7,20}$/.test(String(d.CompanyPhone).trim())) {
    errors.CompanyPhone = 'Usa solo números, espacios, guiones y el prefijo +.';
  }
  return errors;
};

const normalize = (changes) => {
  const out = {};
  Object.keys(changes).forEach((k) => {
    let v = changes[k];
    if (typeof v === 'string') v = v.trim();
    if (k === 'CompanyWebsite' && typeof v === 'string') v = v.replace(/\/+$/, '');
    out[k] = v;
  });
  return out;
};

const AppInformation = ({ onDirtyChange }) => {
  const { editSettings } = api;
  const dispatch = useDispatch();
  const settings = useSelector((state) => state.settingsdata.settings);
  const { draft, setDraft, setField, changes, dirty, discard } = useSettingsDraft(settings);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState(null);
  const canEdit = !!settings?.AllowCriticalEditsAdmin;
  const errors = useMemo(() => validate(draft), [draft]);

  useEffect(() => {
    if (onDirtyChange) onDirtyChange(dirty);
  }, [dirty, onDirtyChange]);

  const handleSave = async () => {
    if (!canEdit) {
      setNotice({ severity: 'info', message: 'Modo demostración: los cambios no se guardan.' });
      return;
    }
    if (Object.keys(changes).some((k) => errors[k])) {
      setNotice({ severity: 'error', message: 'Revisa los campos marcados en rojo antes de guardar.' });
      return;
    }
    const payload = normalize(changes);
    setDraft((d) => ({ ...d, ...payload }));
    setSaving(true);
    try {
      await dispatch(editSettings(payload));
      setNotice({ severity: 'success', message: 'Información de la empresa actualizada.' });
    } catch (e) {
      setNotice({ severity: 'error', message: `No se pudieron guardar los cambios: ${e?.message || e}` });
    } finally {
      setSaving(false);
    }
  };

  if (!draft) return <CircularLoading />;

  const field = (name, label, extra = {}) => (
    <SettingsField name={name} label={label} value={draft[name]} onChange={setField} error={errors[name]} {...extra} />
  );

  return (
    <form onSubmit={(e) => { e.preventDefault(); handleSave(); }}>
      {!canEdit ? <DemoModeAlert /> : null}
      <SettingsNotice notice={notice} onClose={() => setNotice(null)} />
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <SettingsCard icon={BadgeOutlinedIcon} title="Identidad" subtitle="Cómo se presenta la marca en la app, los correos y la web.">
            {field('appName', 'Nombre de la app', { required: true })}
            {field('CompanyName', 'Nombre de la empresa', { required: true })}
          </SettingsCard>
        </Grid>
        <Grid item xs={12} md={6}>
          <SettingsCard icon={ContactPhoneOutlinedIcon} title="Contacto" subtitle="Se muestra en “Acerca de”, “Contáctanos” y en los documentos legales.">
            {field('CompanyPhone', 'Teléfono', { placeholder: '+57 300 000 0000' })}
            {field('contact_email', 'Correo de contacto', { type: 'email', placeholder: 'soporte@conecta2.com' })}
            {field('CompanyAddress', 'Dirección')}
            {field('CompanyWebsite', 'Sitio web', {
              placeholder: 'https://conecta2.com',
              helper: 'Dominio oficial de la web. También se usa para autorizar solicitudes desde ese dominio.',
            })}
          </SettingsCard>
        </Grid>
        <Grid item xs={12} md={6}>
          <SettingsCard icon={GavelOutlinedIcon} title="Documentos legales" subtitle="Enlaces que abren conductores y usuarios desde la app.">
            {field('CompanyTerms', 'Política de privacidad', { placeholder: 'https://…', helper: 'Se abre desde la pantalla de inicio de sesión.' })}
            {field('CompanyTermCondition', 'Términos y condiciones', { placeholder: 'https://…', helper: 'Se abre desde el mapa y los viajes del conductor.' })}
          </SettingsCard>
        </Grid>
        <Grid item xs={12} md={6}>
          <SettingsCard icon={StorefrontOutlinedIcon} title="Tiendas de apps" subtitle="Enlaces de descarga que aparecen en la web.">
            {field('PlayStoreLink', 'Google Play', { placeholder: 'https://play.google.com/store/apps/details?id=…' })}
            {field('AppleStoreLink', 'App Store', { placeholder: 'https://apps.apple.com/…' })}
          </SettingsCard>
        </Grid>
        <Grid item xs={12}>
          <SettingsCard icon={ShareOutlinedIcon} title="Redes sociales" subtitle="Enlaces completos a los perfiles oficiales. Déjalos vacíos para ocultarlos.">
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>{field('FacebookHandle', 'Facebook', { placeholder: 'https://facebook.com/…' })}</Grid>
              <Grid item xs={12} md={4}>{field('InstagramHandle', 'Instagram', { placeholder: 'https://instagram.com/…' })}</Grid>
              <Grid item xs={12} md={4}>{field('TwitterHandle', 'X (Twitter)', { placeholder: 'https://x.com/…' })}</Grid>
            </Grid>
          </SettingsCard>
        </Grid>
      </Grid>
      <SettingsSaveBar dirty={dirty} saving={saving} onSave={handleSave} onDiscard={discard} disabled={!canEdit} />
    </form>
  );
};

export default AppInformation;
